import { Button, OrbVisualizer } from '@qi/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_funnel/start/calibrate')({
  beforeLoad: () => {
    const { faceSummary, voiceSummary } = useSessionStore.getState();
    if (!faceSummary || !voiceSummary) throw redirect({ to: '/start' });
  },
  component: FunnelCalibratePage,
});

const DURATION_S = 30;
// Gentle, sub-aggressive tone. A pure sine at a calm mid frequency with a soft
// gain envelope — this is a felt cue, not a measured frequency claim.
const TONE_HZ = 432;

function FunnelCalibratePage() {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(DURATION_S);
  const audioRef = useRef<{ ctx: AudioContext; osc: OscillatorNode; gain: GainNode } | null>(null);

  const stopTone = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.gain.gain.linearRampToValueAtTime(0, a.ctx.currentTime + 0.4);
      a.osc.stop(a.ctx.currentTime + 0.5);
      setTimeout(() => a.ctx.close().catch(() => {}), 600);
    } catch {
      // Context already torn down — nothing to do.
    }
    audioRef.current = null;
  }, []);

  function startCalibration() {
    setRunning(true);
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = TONE_HZ;
      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 1.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      audioRef.current = { ctx, osc, gain };
    } catch {
      // Audio unavailable — the visual + timer still carry the session.
    }
  }

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (running && remaining === 0) {
      stopTone();
      const id = setTimeout(() => navigate({ to: '/start/improved' }), 700);
      return () => clearTimeout(id);
    }
  }, [running, remaining, navigate, stopTone]);

  // Stop the tone if the user navigates away mid-session.
  useEffect(() => () => stopTone(), [stopTone]);

  const progress = (DURATION_S - remaining) / DURATION_S;

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center space-y-8 px-6 py-12 text-center">
      <header className="space-y-3">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-aurora">
          {running ? 'Calibrating' : 'Calibration'}
        </p>
        <h1 className="font-display text-3xl text-ink-0">Calibrating your resonance</h1>
        <p className="text-pretty text-ink-1">
          Gently stabilizing and optimizing your bio-field. Sit comfortably, soften your breath, and
          let the tone settle.
        </p>
      </header>

      <div className="relative flex items-center justify-center">
        <OrbVisualizer size={220} rings={4} />
        {running && (
          <span className="absolute font-mono text-4xl tabular-nums text-ink-0">{remaining}</span>
        )}
      </div>

      {!running ? (
        <Button variant="resonance" size="lg" className="w-full" onClick={startCalibration}>
          Begin calibration
        </Button>
      ) : (
        <div className="w-full space-y-2" aria-live="polite">
          <div className="h-1 w-full overflow-hidden rounded-pill bg-surface-3">
            <div
              className="h-full transition-[width] duration-1000 ease-linear"
              style={{
                width: `${progress * 100}%`,
                background: 'linear-gradient(90deg, var(--violet), var(--aurora))',
              }}
            />
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            Stay with it — {remaining}s remaining
          </p>
        </div>
      )}
    </div>
  );
}
